import { HttpApiError } from "@effect/platform";
import type { UserPermissions } from "@redotech/account-common/permissions";
import { userPermissions } from "@redotech/account-common/permissions";
import { Cause, Context, Effect, Option, Schema } from "effect";
import { AccountId } from "../ids/definitions";
import { MongoTeamId, MongoUserId } from "../ids/mongo-ids";
import { DatabaseError, DataIntegrityError } from "./errors";

const UserPermissionsSchema = Schema.Literal(...userPermissions);

const authedHandlerErrorLogTags = new Set([
  "ConfigError",
  "InternalServerError",
  "ParseError",
  "ServiceUnavailable",
  "SqlError",
]);

const getErrorTag = (error: unknown): string | undefined =>
  typeof error === "object" && error !== null && "_tag" in error
    ? String((error as { _tag?: unknown })._tag)
    : undefined;

const shouldLogAuthedHandlerFailure = (
  cause: Cause.Cause<unknown>,
): boolean => {
  const failure = Option.getOrUndefined(Cause.failureOption(cause));
  const errorTag = getErrorTag(failure);
  if (errorTag) {
    return authedHandlerErrorLogTags.has(errorTag);
  }
  return Option.isSome(Cause.dieOption(cause));
};

export class AuthenticatedTeam extends Schema.Class<AuthenticatedTeam>(
  "AuthenticatedTeam",
)({
  _id: MongoTeamId,
  name: Schema.String,
  accountId: AccountId,
  timezone: Schema.optional(Schema.String),
  // Optional so auth contexts cached before this field existed still decode;
  // `undefined` is treated as unified inbox enabled.
  allowUnifiedInbox: Schema.optional(Schema.Boolean),
}) {}

export class AuthenticatedUser extends Schema.Class<AuthenticatedUser>(
  "AuthenticatedUser",
)({
  _id: MongoUserId,
  email: Schema.String,
  firstName: Schema.optional(Schema.String),
  lastName: Schema.optional(Schema.String),
  isRedoEmployee: Schema.optional(Schema.Boolean),
  managedUserRoles: Schema.optional(Schema.Array(Schema.String)),
}) {}

export class AuthContext extends Schema.Class<AuthContext>("AuthContext")({
  team: AuthenticatedTeam,
  user: AuthenticatedUser,
  permissions: Schema.Array(UserPermissionsSchema),
}) {
  /**
   * Check if user has any of the required permissions (OR logic).
   * @returns True if user has at least one of the required permissions
   */
  hasPermissions(
    requiredPermissions: UserPermissions | UserPermissions[],
  ): boolean {
    const required = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];
    if (required.length === 0) {
      return true;
    }

    const userPermissions = new Set(this.permissions);
    return required.some((p) => userPermissions.has(p));
  }

  /**
   * Require user to have any of the specified permissions (OR logic).
   * Fails with Forbidden if user lacks all required permissions.
   */
  requirePermissions(
    requiredPermissions: UserPermissions | UserPermissions[],
  ): Effect.Effect<void, HttpApiError.Forbidden> {
    return Effect.gen(this, function* () {
      if (!this.hasPermissions(requiredPermissions)) {
        return yield* new HttpApiError.Forbidden();
      }
    });
  }
}

export class CurrentUser extends Context.Tag("CurrentUser")<
  CurrentUser,
  AuthContext
>() {
  static withPermissions(
    requiredPermissions: UserPermissions | UserPermissions[],
  ): Effect.Effect<AuthContext, HttpApiError.Forbidden, CurrentUser> {
    return Effect.gen(function* () {
      const user = yield* CurrentUser;
      yield* user.requirePermissions(requiredPermissions);
      return user;
    });
  }

  static authedHandler<A, E, R>(
    /**
     * Name of the handler for tracing purposes.
     */
    name: string,
    /**
     * Permissions required to execute this handler.
     * If the current user lacks all of these permissions, the handler will fail with Forbidden.
     */
    requiredPermissions: UserPermissions | UserPermissions[] | null,
    /**
     * The actual handler effect to execute once authorization checks have passed.
     */
    effect: (authCtx: AuthContext) => Effect.Effect<A, E, R>,
  ): Effect.Effect<
    A,
    | Exclude<E, { _tag: "SqlError" | "ParseError" | "ConfigError" }>
    | HttpApiError.Forbidden
    | DatabaseError
    | DataIntegrityError,
    R | CurrentUser
  > {
    return Effect.gen(function* () {
      const user = yield* requiredPermissions
        ? CurrentUser.withPermissions(requiredPermissions)
        : CurrentUser;
      return yield* effect(user).pipe(
        Effect.withSpan(name),
        Effect.annotateSpans({ userId: user.user._id, teamId: user.team._id }),
        Effect.annotateLogs({ userId: user.user._id, teamId: user.team._id }),
        // Surface infrastructure errors before mapping them to opaque
        // public-facing error tags. Without this, SqlError/ParseError/ConfigError
        // disappear into a generic "Database error occurred" with no log trail.
        // Effect's SqlError stringifies to just "Failed to execute statement"
        // — the actual pg error lives on `.cause`, so unwrap it for the log.
        Effect.tapErrorCause((cause) => {
          if (!shouldLogAuthedHandlerFailure(cause)) {
            return Effect.void;
          }

          const failure = Option.getOrUndefined(Cause.failureOption(cause)) as
            | {
                _tag?: string;
                message?: string;
                cause?: { message?: string } | string;
              }
            | undefined;
          const causeMessage =
            failure?.cause && typeof failure.cause === "object"
              ? (failure.cause as { message?: string }).message
              : typeof failure?.cause === "string"
                ? failure.cause
                : undefined;
          return Effect.logError("Authed handler failed").pipe(
            Effect.annotateLogs({
              handler: name,
              errorTag: failure?._tag,
              errorMessage: failure?.message,
              underlyingCause: causeMessage,
              cause: String(cause),
            }),
          );
        }),
        Effect.catchTags({
          SqlError: () =>
            Effect.fail(
              new DatabaseError({ message: "Database error occurred" }),
            ),
          ParseError: () =>
            Effect.fail(
              new DataIntegrityError({
                message: "Data integrity error occurred",
              }),
            ),
          ConfigError: () =>
            Effect.fail(
              new DatabaseError({ message: "Configuration error occurred" }),
            ),
        }),
      );
    });
  }
}
